import { type ReferentPriceType } from '@strata/constants';
import { type GridWithFilters } from '@strata/react-grid';
import {
    type ReferralStatusIndicator,
    type TreatmentStatusIndicator,
} from '@strata/react-status-indicators';
import { type ComponentProps } from 'react';

import { APERTURE_API_URL, fmhNpiAPI } from 'api/APIBase';

export interface ReferralSummary {
    referral_id: string;
    treatment_id?: string;
    segment_id: string;
    member_first_name: string;
    member_last_name: string;
    member_id: string;
    member_dob: string;
    product_service_description: string;
    supplier_npi?: string;
    prescriber_npi: string;
    winning_bid_rate?: string;
    referent_price_type: ReferentPriceType;
    referral_status: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
    member_enrollment_status?: string;
    received_date: string;
    referral_start: string;
    is_brand: boolean;
    therapeutic_category?: string;
}

export interface TreatmentSummary {
    treatment_id: string;
    current_segment_id: string;
    member_id: string;
    payer_id: string;
    member_first_name: string;
    member_last_name: string;
    product_service_oid: string;
    treatment_status: (typeof TreatmentStatusIndicator.Statuses)[keyof typeof TreatmentStatusIndicator.Statuses];
    restricted_group?: string;
}

/** Gets filtered referrals for payer */
export async function getReferrals({
    segmentId,
    page,
    supplierNpi,
    prescriberNpi,
    searchText,
    activeFilters,
}: {
    segmentId?: string;
    page: number;
    supplierNpi?: string;
    prescriberNpi?: string;
    searchText?: string;
    activeFilters: ComponentProps<typeof GridWithFilters>['activeFilters'];
}) {
    const url = `${APERTURE_API_URL}/referrals`;
    return fmhNpiAPI({ url })
        .query({
            ...(segmentId ? { segment_id: segmentId } : {}),
            page,
            ...(supplierNpi ? { pharmacy_npi: supplierNpi } : {}),
            ...(prescriberNpi ? { prescriber_npi: prescriberNpi } : {}),
            ...(searchText ? { search_text: searchText } : {}),
            ...activeFilters,
        })
        .get()
        .json<{
            count?: number;
            filtered_count: number;
            filters?: { key: keyof ReferralSummary; values: string[] }[];
            referrals: ReferralSummary[];
        }>();
}

/** Gets referrals for a patient */
export async function getTreatmentsForPatient({
    memberId,
    memberFirstName,
    memberLastName,
    memberDob,
}: {
    memberId?: string;
    memberFirstName?: string;
    memberLastName?: string;
    memberDob?: string | Date;
}) {
    const url = `${APERTURE_API_URL}/treatments`;
    return fmhNpiAPI({ url })
        .query({
            ...(memberId ? { member_id: memberId } : {}),
            ...(memberFirstName ? { member_first_name: memberFirstName } : {}),
            ...(memberLastName ? { member_last_name: memberLastName } : {}),
            ...(memberDob ? { member_dob: memberDob } : {}),
        })
        .get()
        .json<{ treatments: TreatmentSummary[] }>();
}

/** Checks if a group ID is restricted */
export async function getCheckRestrictedGroup({
    groupId,
    memberId,
    memberFirstName,
    memberLastName,
    memberDob,
}: {
    groupId: string;
    memberId?: string;
    memberFirstName?: string;
    memberLastName?: string;
    memberDob?: string | Date;
}) {
    const url = `${APERTURE_API_URL}/restricted_group_check/${groupId}`;
    return fmhNpiAPI({ url })
        .query({
            ...(memberId ? { member_id: memberId } : {}),
            ...(memberFirstName ? { member_first_name: memberFirstName } : {}),
            ...(memberLastName ? { member_last_name: memberLastName } : {}),
            ...(memberDob ? { member_dob: memberDob } : {}),
        })
        .get()
        .json<boolean>();
}
